import { useQuery } from "@tanstack/react-query";
import {
  getSocialProfilesByIdIdentityUserPublicID,
  getSocialProfilesHandle,
  type PublicProfileDto,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

const PUBLIC_PROFILE_QUERY_KEY = "SOCIAL_PUBLIC_PROFILE";

/**
 * How a profile is addressed on /profile/{key}: by handle (the pretty variant) or by
 * persona PublicID (the only address of handle-less users). The hosting route validates
 * the key shape before constructing the lookup.
 */
export type ProfileLookup =
  | { handle: string }
  | { identityUserPublicID: string };

export const publicProfileKeys = {
  all: [PUBLIC_PROFILE_QUERY_KEY] as const,
  byHandle: (handle: string) => [...publicProfileKeys.all, handle] as const,
  byId: (identityUserPublicID: string) =>
    [...publicProfileKeys.all, "by-id", identityUserPublicID] as const,
};

/**
 * Anonymous lookup behind the /profile/{key} URL. A 404 is an expected outcome
 * (unknown handle/ID), so retries are disabled and the page renders the not-found state;
 * the global reporting rule keeps that 404 as a breadcrumb.
 */
export const usePublicProfileQuery = (lookup: ProfileLookup) =>
  useQuery<PublicProfileDto>({
    queryKey:
      "handle" in lookup
        ? publicProfileKeys.byHandle(lookup.handle)
        : publicProfileKeys.byId(lookup.identityUserPublicID),
    queryFn: processResponseWithApiProblemDetails(() =>
      "handle" in lookup
        ? getSocialProfilesHandle(lookup.handle)
        : getSocialProfilesByIdIdentityUserPublicID(
            lookup.identityUserPublicID,
          ),
    ),
    retry: false,
  });
